/*
 * Bot messages are stored as HTML - the chat frame renders them as markup, so
 * `<strong>`, `<em>` and `<a>` written into a message reach the visitor
 * formatted. The editor shows that markup formatted instead of as tags; these
 * helpers turn a stored message into what the editor shows, and back.
 */

import {SAFE_URL} from "../../../../../../../../utils/Html";

export {PLACEHOLDERS, sanitizeHtml} from "../../../../../../../../utils/Html";

const BLOCK_TAGS = ['DIV', 'P', 'LI'];

/*
 * An invisible character the editor puts right after formatted text, so the
 * caret can stand outside it (MessageEditor's leaveFormattingAtCaret). It
 * only ever exists in the editor - saving drops it.
 */
export const ZERO_WIDTH_SPACE = String.fromCharCode(0x200b);

const escapeAttribute = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/*
 * `&` is escaped only where it would otherwise be read as an entity, so a
 * message without formatting is saved exactly as it was typed - the same text
 * also goes to messengers that do not render HTML.
 */
const escapeText = (value) => value
    .split(ZERO_WIDTH_SPACE).join('')
    .replace(/\u00A0/g, ' ')
    .replace(/&(?=#?\w+;)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // <first_name> / <last_name> go to the backend as written, or it cannot find them
    .replace(/&lt;(first_name|last_name)&gt;/g, '<$1>');

export const normalizeUrl = (raw) => {
    const url = (raw || '').trim();
    if (!url) return '';
    if (SAFE_URL.test(url)) return url;
    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return ''; // javascript:, data: and the like
    return `https://${url}`;
};

/*
 * What the editor holds, as the message to store. Browsers write bold and
 * italic as `<b>` / `<i>` and break lines with `<br>` or whole `<div>`s; the
 * message gets `<strong>`, `<em>` and plain newlines, as it always had.
 */
export const serializeEditor = (root) => {
    let out = '';

    const walk = (node) => {
        Array.from(node.childNodes).forEach((child) => {
            if (child.nodeType === 3) {
                out += escapeText(child.nodeValue);
                return;
            }
            if (child.nodeType !== 1) return;

            const tag = child.tagName;

            if (tag === 'BR') {
                out += '\n';
                return;
            }

            if (BLOCK_TAGS.indexOf(tag) !== -1) {
                if (out && !out.endsWith('\n')) out += '\n';
                walk(child);
                return;
            }

            const wrap = {STRONG: 'strong', B: 'strong', EM: 'em', I: 'em', U: 'u'}[tag];
            if (wrap) {
                const inner = serializeChildren(child);
                if (inner) out += `<${wrap}>${inner}</${wrap}>`;
                return;
            }

            if (tag === 'A') {
                const href = normalizeUrl(child.getAttribute('href'));
                const inner = serializeChildren(child);
                if (!inner) return;
                out += href ? `<a href="${escapeAttribute(href)}" target="_blank">${inner}</a>` : inner;
                return;
            }

            walk(child); // span and anything else a paste or the browser left behind
        });
    };

    const serializeChildren = (node) => {
        const saved = out;
        out = '';
        walk(node);
        const inner = out;
        out = saved;
        return inner;
    };

    walk(root);

    // a line break the browser keeps at the very end only so the caret has somewhere to go
    return out.replace(/\n+$/, '');
};



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/NodeEditor/components/MessageEditor/formatting.js